// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  Alert,
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { isRpmMonthRequirementMet, isRpmSyncRequirementMet, RPM_REQUIREMENT_THRESHOLDS } from '@polarissleep/shared';
import { IconAlertCircle, IconCheck, IconChevronDown, IconTriangleInvertedFilled } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useState } from 'react';

import { TableAnchor } from '../../../components/layout/TableAnchor';
import { usePatientRpmHistory } from '../../../hooks/usePatientRpmHistory';
import { formatSyncRequirement, getTotalMinutesColor } from '../../../utils/rpmNotes';
import { RpmMonthNotesModal } from '../../triage/RpmMonthNotesModal';

function formatMonth(value: string): string {
  return dayjs(`${value}-01`).format('MMMM YYYY');
}

function formatDate(value: string | undefined): string {
  return value ? dayjs(value).format('MM/DD/YYYY') : '—';
}

function formatStatusLabel(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getStatusColor(value: string | undefined): string {
  switch (value) {
    case 'adherent':
      return 'green';
    case 'low-usage':
      return 'orange';
    case 'below-threshold':
      return 'yellow';
    case 'non-responsive':
      return 'red';
    default:
      return 'gray';
  }
}

function getAdherenceBarColor(value: number | undefined): string {
  if (value === undefined) {
    return 'gray';
  }
  if (value >= 28) {
    return 'green';
  }
  if (value >= 24) {
    return 'lime';
  }
  return 'orange';
}

function RequirementBadge({ complete }: { complete: boolean }): JSX.Element {
  return (
    <Badge
      color={complete ? 'green' : 'orange'}
      variant="light"
      radius="xl"
      leftSection={complete ? <IconCheck size={12} /> : undefined}
      styles={{
        root: { textTransform: 'none', fontWeight: 700 },
      }}
    >
      {complete ? 'Complete' : 'Incomplete'}
    </Badge>
  );
}

function TotalMinutesBadge({
  asyncMinutes,
  syncMinutes,
}: {
  asyncMinutes: number | undefined;
  syncMinutes: number | undefined;
}): JSX.Element {
  if (asyncMinutes === undefined && syncMinutes === undefined) {
    return <>—</>;
  }
  const totalMinutes = (asyncMinutes ?? 0) + (syncMinutes ?? 0);
  return (
    <Badge
      color={getTotalMinutesColor(totalMinutes)}
      variant="light"
      radius="xl"
      styles={{
        root: { textTransform: 'none', fontWeight: 700 },
      }}
    >
      {totalMinutes} min
    </Badge>
  );
}

interface RpmHistorySectionProps {
  patient: Patient;
}

export function RpmHistorySection({ patient }: RpmHistorySectionProps): JSX.Element {
  const { rows, loading } = usePatientRpmHistory(patient);
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();
  const patientName = patient.name?.[0]?.given?.join(' ')
    ? `${patient.name?.[0]?.given?.join(' ')} ${patient.name?.[0]?.family ?? ''}`.trim()
    : patient.id;
  const latestRow = rows[0];
  const latestComplete = latestRow ? isRpmMonthRequirementMet(latestRow.asyncMinutes, latestRow.syncMinutes) : false;

  let content: JSX.Element;
  if (loading) {
    content = (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  } else if (rows.length === 0) {
    content = (
      <Text c="dimmed" ta="center" py="md">
        No RPM history on file.
      </Text>
    );
  } else {
    content = (
      <Box style={{ overflowX: 'auto' }}>
        <Table
          highlightOnHover
          withTableBorder
          styles={{
            th: {
              backgroundColor: '#faf7f8',
              color: '#4f1735',
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            },
            td: {
              verticalAlign: 'middle',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Month</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Adherence (30-day)</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Last contact</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Total minutes</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Sync req.</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Status</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={6} wrap="nowrap">
                  <span>Actions</span>
                  <IconChevronDown size={12} color="#b08495" />
                </Group>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => {
              const monthComplete = isRpmMonthRequirementMet(row.asyncMinutes, row.syncMinutes);
              const adherenceValue = row.adherenceNights ?? 0;
              return (
                <Table.Tr key={row.month}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={700} c="#1f2937">
                        {formatMonth(row.month)}
                      </Text>
                      <Badge
                        color={monthComplete ? 'green' : 'orange'}
                        variant="light"
                        radius="xl"
                        leftSection={
                          monthComplete ? (
                            <IconCheck size={10} />
                          ) : (
                            <IconTriangleInvertedFilled size={8} style={{ transform: 'rotate(180deg)' }} />
                          )
                        }
                        styles={{
                          root: { textTransform: 'none', fontWeight: 700 },
                        }}
                      >
                        {monthComplete ? 'Complete' : 'Incomplete'}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {typeof row.adherenceNights === 'number' ? (
                      <Group gap="sm" wrap="nowrap">
                        <Progress
                          value={(adherenceValue / 30) * 100}
                          color={getAdherenceBarColor(row.adherenceNights)}
                          radius="xl"
                          size="sm"
                          style={{ flex: '0 0 88px' }}
                        />
                        <Text fw={700} c="#1f2937">
                          {row.adherenceNights} / 30
                        </Text>
                      </Group>
                    ) : (
                      '—'
                    )}
                  </Table.Td>
                  <Table.Td>{formatDate(row.lastContactDate)}</Table.Td>
                  <Table.Td>
                    <TotalMinutesBadge asyncMinutes={row.asyncMinutes} syncMinutes={row.syncMinutes} />
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={4}>
                      <RequirementBadge complete={isRpmSyncRequirementMet(row.syncMinutes)} />
                      <Text size="sm" fw={600} c="dimmed">
                        {formatSyncRequirement(row.syncMinutes)}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={getStatusColor(row.monitoringStatus)}
                      variant="light"
                      radius="xl"
                      styles={{
                        root: { textTransform: 'none', fontWeight: 700 },
                      }}
                    >
                      {formatStatusLabel(row.monitoringStatus)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <TableAnchor component="button" type="button" size="sm" onClick={() => setSelectedMonth(row.month)}>
                      View Note
                    </TableAnchor>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>
    );
  }

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Group gap="sm" wrap="nowrap">
          <Title order={2}>Remote Patient Monitoring</Title>
          <Badge variant="default" radius="xl">
            {rows.length}
          </Badge>
        </Group>
      </Group>
      {!loading && rows.length > 0 && (
        <>
          <Paper
            withBorder
            radius="md"
            p="md"
            mb="md"
            style={{
              background: '#fbf5f8',
              borderColor: '#ead7e2',
            }}
          >
            <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg" verticalSpacing="md">
              <div>
                <Text size="xs" fw={800} c="#7d4760" tt="uppercase">
                  Status
                </Text>
                <Text fw={800} c={latestComplete ? 'green' : 'orange'} mt={4}>
                  {latestComplete ? 'Active' : 'Incomplete'}
                </Text>
              </div>
              <div>
                <Text size="xs" fw={800} c="#7d4760" tt="uppercase">
                  Last Contact
                </Text>
                <Text fw={800} c="#4f1735" mt={4}>
                  {formatDate(latestRow?.lastContactDate)}
                </Text>
              </div>
              <div>
                <Text size="xs" fw={800} c="#7d4760" tt="uppercase">
                  Latest Status
                </Text>
                <Text fw={800} c="#4f1735" mt={4}>
                  {formatStatusLabel(latestRow?.monitoringStatus)}
                </Text>
              </div>
              <div>
                <Text size="xs" fw={800} c="#7d4760" tt="uppercase">
                  Months Tracked
                </Text>
                <Text fw={800} c="#4f1735" mt={4}>
                  {rows.length}
                </Text>
              </div>
            </SimpleGrid>
          </Paper>
          <Alert
            color="gray"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            mb="md"
            styles={{
              root: {
                background: '#ffffff',
                border: '1px solid #e6e8ef',
              },
              label: {
                color: '#374151',
                fontWeight: 500,
              },
            }}
          >
            Each month&apos;s log locks when the month ends. Closed months are read-only. A month that ends with fewer
            than {RPM_REQUIREMENT_THRESHOLDS.totalMinutes} total minutes, or without a synchronous contact, is marked
            incomplete.
          </Alert>
        </>
      )}
      {content}

      <RpmMonthNotesModal
        patientId={patient.id}
        patientName={patientName}
        month={selectedMonth}
        opened={Boolean(selectedMonth)}
        onClose={() => setSelectedMonth(undefined)}
      />
    </Paper>
  );
}
